// Admin "Complete case" workflow — finalize counts, preview the
// closeout invoice, and fire complete_case_and_finalize. The DB webhook
// on competitions UPDATE picks up the status transition and invokes
// create-stripe-final-invoice automatically.
//
// Access: super_admin / admin_licensing / admin_rm (engagement-tier
// admins). admin_licensing can only complete licensing cases; admin_rm
// can only complete RM cases — the RPC re-validates server-side.

import type { Href } from "expo-router";
import { router, useFocusEffect, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform, Pressable, ScrollView, Text, View, type ViewStyle } from "react-native";

import { useAuth } from "../../../../../lib/authContext";
import { logAdminAction } from "../../../../../lib/auditLog";
import {
  formatCents,
  getCaseForReview,
  type CaseForReview,
} from "../../../../../lib/caseApprovals";
import {
  completeCaseAndFinalize,
  computeFinalInvoicePreview,
  getCaseCountsForCompletion,
  getFinalInvoiceForCase,
  regenerateFinalInvoice,
  type FinalInvoiceLine,
  type InvoiceRow,
} from "../../../../../lib/finalInvoice";
import { RADII, SPACING, STROKES } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";
import {
  AdminBanner,
  AdminButton,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
} from "../../../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;

export default function AdminCompleteCaseScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { role } = useAuth();
  const canAccess = role === "super_admin" || role === "admin_licensing" || role === "admin_rm";

  const [detail, setDetail] = useState<CaseForReview | null>(null);
  const [existingFinal, setExistingFinal] = useState<InvoiceRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [studentCount, setStudentCount] = useState("");
  const [submissionCount, setSubmissionCount] = useState("");
  const [presentationCount, setPresentationCount] = useState("");
  const [netTerms, setNetTerms] = useState("30");
  const [notes, setNotes] = useState("");
  const [autoDefaults, setAutoDefaults] = useState<{ students: number; submissions: number }>({ students: 0, submissions: 0 });

  const [preview, setPreview] = useState<FinalInvoiceLine[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyRegen, setBusyRegen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  const load = useCallback(async () => {
    if (typeof id !== "string") return;
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [d, fi, counts] = await Promise.all([
        getCaseForReview(id),
        getFinalInvoiceForCase(id),
        getCaseCountsForCompletion(id),
      ]);
      setDetail(d);
      setExistingFinal(fi);
      setAutoDefaults({ students: counts.registered_students, submissions: counts.total_submissions });
      if (!studentCount) setStudentCount(String(counts.registered_students));
      if (!submissionCount) setSubmissionCount(String(counts.total_submissions));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));

  // Live preview as the admin types. Debounced so we don't hammer the
  // RPC on every keystroke. Computes only when counts parse to numbers.
  useEffect(() => {
    if (typeof id !== "string") return;
    if (!detail) return;
    const s = parseInt(studentCount, 10);
    const m = parseInt(submissionCount, 10);
    if (!Number.isFinite(s) || s < 0 || !Number.isFinite(m) || m < 0) {
      setPreview([]);
      setPreviewErr(null);
      return;
    }
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewErr(null);
      try {
        const isRm = detail.engagement_model === "rm";
        const p = isRm ? parseInt(presentationCount, 10) : null;
        const lines = await computeFinalInvoicePreview({
          caseId: id,
          studentCount: s,
          submissionCount: m,
          teamPresentationCount: isRm && Number.isFinite(p) && p! >= 0 ? p : null,
        });
        setPreview(lines);
      } catch (e) {
        setPreviewErr(e instanceof Error ? e.message : "Preview failed");
        setPreview([]);
      } finally {
        setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [studentCount, submissionCount, presentationCount, detail, id]);

  const subtotalCents = useMemo(
    () => preview.reduce((s, l) => s + (typeof l.subtotal_cents === "number" ? l.subtotal_cents : 0), 0),
    [preview],
  );

  async function onFinalize() {
    if (typeof id !== "string" || !detail) return;
    setBusy(true);
    setErr(null);
    setToast(null);
    try {
      const s = parseInt(studentCount, 10);
      const m = parseInt(submissionCount, 10);
      const p = parseInt(presentationCount, 10);
      const isRm = detail.engagement_model === "rm";
      const net = Math.max(1, parseInt(netTerms, 10) || 30);
      await completeCaseAndFinalize({
        caseId: id,
        studentCount: s,
        submissionCount: m,
        teamPresentationCount: isRm && Number.isFinite(p) ? p : null,
        completionNotes: notes.trim() || null,
        netTermsDays: net,
      });
      void logAdminAction({
        action: "case.finalized",
        entityType: "competitions",
        entityId: id,
        summary: `Finalized case: ${detail.name} (${s} students, ${m} submissions)`,
        diff: {
          after: {
            final_student_count: s,
            final_submission_count: m,
            final_team_presentation_count: isRm ? p : null,
            final_invoice_amount_cents: subtotalCents,
          },
        },
      });
      setToast("Case finalized. Final invoice is being generated and will appear in the client's billing tab.");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function onRegenerate() {
    if (typeof id !== "string") return;
    setBusyRegen(true);
    setErr(null);
    setToast(null);
    try {
      await regenerateFinalInvoice(id);
      void logAdminAction({
        action: "case.final_invoice_regenerated",
        entityType: "competitions",
        entityId: id,
        summary: `Regenerated final invoice for ${detail?.name ?? "case"}`,
      });
      setToast("Final invoice regenerated.");
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Regenerate failed");
    } finally {
      setBusyRegen(false);
    }
  }

  if (!canAccess) {
    return (
      <View style={{ flex: 1, backgroundColor: THEME.pageBg, padding: SPACING.lg }}>
        <AdminBanner
          theme={THEME}
          tone="error"
          title="Access denied"
          message="Case finalization is restricted to super_admin / admin_licensing / admin_rm."
        />
      </View>
    );
  }
  if (loading || !detail) {
    return (
      <View style={{ flex: 1, justifyContent: "center", backgroundColor: THEME.pageBg }}>
        <AdminLoading theme={THEME} label="Loading case…" />
      </View>
    );
  }

  const isRm = detail.engagement_model === "rm";
  const alreadyFinalized = detail.status === "final_invoice_pending" || detail.status === "final_invoice_paid";

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 800 }}>
        <BackLink />

        <AdminPageHeader
          theme={THEME}
          eyebrow="Inbox · Complete case"
          title="Complete this case"
          subtitle={`${detail.name} · ${detail.company_name ?? "—"}`}
        />

        {err ? (
          <View style={{ marginBottom: SPACING.md }}>
            <AdminError theme={THEME} message={err} onRetry={() => void load()} />
          </View>
        ) : null}
        {toast ? (
          <View style={{ marginBottom: SPACING.md }}>
            <AdminBanner theme={THEME} tone="success" message={toast} />
          </View>
        ) : null}

        {/* Already-finalized state */}
        {alreadyFinalized && existingFinal ? (
          <View style={{ padding: 18, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
            <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: THEME.success }}>
              Already finalized
            </Text>
            <Text style={{ marginTop: 6, fontFamily: THEME.displayMediumFont, fontSize: 20, color: THEME.textOnSurface }}>
              {formatCents(existingFinal.total_cents)} · {existingFinal.status}
            </Text>
            <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
              Due {existingFinal.due_date ? new Date(existingFinal.due_date).toLocaleDateString() : "—"}
              {existingFinal.paid_at ? ` · Paid ${new Date(existingFinal.paid_at).toLocaleDateString()}` : ""}
            </Text>
            <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              {existingFinal.stripe_invoice_hosted_url ? (
                <AdminButton
                  theme={THEME}
                  label="View invoice ↗"
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    if (Platform.OS === "web" && typeof window !== "undefined") {
                      window.open(existingFinal.stripe_invoice_hosted_url!, "_blank");
                    }
                  }}
                />
              ) : null}
              {existingFinal.status === "pending" || existingFinal.status === "failed" ? (
                <AdminButton
                  theme={THEME}
                  label="Regenerate final invoice"
                  size="sm"
                  loading={busyRegen}
                  onPress={() => void onRegenerate()}
                />
              ) : null}
            </View>
          </View>
        ) : null}

        {/* Completion form — visible when not yet finalized, or when
            voided and the admin wants to re-issue. */}
        {!alreadyFinalized || existingFinal?.status === "voided" ? (
          <View style={{ padding: 18, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
            <Text style={{ fontFamily: THEME.displayMediumFont, fontSize: 20, color: THEME.textOnSurface }}>Final counts</Text>
            <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
              Submit final counts to generate the closeout invoice. Stripe Tax is computed automatically at invoice time.
            </Text>

            <View style={{ marginTop: SPACING.md, gap: 12 }}>
              <NumField
                label="Students registered"
                value={studentCount}
                onChange={setStudentCount}
                hint={`Auto: ${autoDefaults.students} from registrations`}
              />
              <NumField
                label="Total submissions"
                value={submissionCount}
                onChange={setSubmissionCount}
                hint={`Auto: ${autoDefaults.submissions} from submissions`}
              />
              {isRm ? (
                <NumField
                  label="Team presentations"
                  value={presentationCount}
                  onChange={setPresentationCount}
                  hint="RM only — enter the count of team presentations delivered"
                />
              ) : null}
              <NumField
                label="Net terms (days)"
                value={netTerms}
                onChange={setNetTerms}
                hint="Default 30. Stripe sends a follow-up reminder before due date."
              />
              <AdminInput
                theme={THEME}
                label="Completion notes (optional)"
                value={notes}
                onChangeText={setNotes}
                multiline
                minHeight={60}
                placeholder="Anything to record on the audit trail."
              />
            </View>

            {/* Preview */}
            <Text style={{ marginTop: 18, fontFamily: THEME.bodyBoldFont, fontSize: 9.5, letterSpacing: 1.6, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
              Preview
            </Text>
            <View style={{ marginTop: 8, padding: 12, borderRadius: RADII.sm, borderWidth: STROKES.hairline, borderColor: THEME.border, backgroundColor: THEME.altBg, gap: 6 }}>
              {previewLoading ? (
                <AdminLoading theme={THEME} />
              ) : previewErr ? (
                <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 12, color: THEME.destructive }}>{previewErr}</Text>
              ) : preview.length === 0 ? (
                <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
                  Enter counts above to see the math.
                </Text>
              ) : (
                <>
                  {preview.map((line, idx) => (
                    <View key={idx} style={{ flexDirection: "row", justifyContent: "space-between", gap: 8 }}>
                      <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
                        {line.line_item_label}
                      </Text>
                      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 12, color: THEME.textOnSurface }}>
                        = {formatCents(line.subtotal_cents)}
                      </Text>
                    </View>
                  ))}
                  <View style={{ marginTop: 4, paddingTop: 8, borderTopWidth: STROKES.hairline, borderTopColor: THEME.border, flexDirection: "row", justifyContent: "space-between" }}>
                    <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 11, letterSpacing: 1.3, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
                      Subtotal
                    </Text>
                    <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 13, color: THEME.textOnSurface }}>{formatCents(subtotalCents)}</Text>
                  </View>
                  <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 10.5, color: THEME.textOnSurfaceFaint }}>
                    Sales tax computed by Stripe at invoice time.
                  </Text>
                </>
              )}
            </View>

            <View style={{ marginTop: 18, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <AdminButton
                theme={THEME}
                label="Generate final invoice"
                loading={busy}
                disabled={preview.length === 0}
                onPress={() => void onFinalize()}
              />
              <AdminButton theme={THEME} label="Cancel" variant="ghost" onPress={() => router.back()} />
            </View>
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
}

function BackLink() {
  return (
    <Pressable
      onPress={() => {
        if (router.canGoBack()) router.back();
        else router.replace("/admin/case-approvals" as Href);
      }}
      style={[
        { alignSelf: "flex-start", paddingVertical: 4, marginBottom: 6 } as ViewStyle,
        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
      ]}
    >
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11.5, letterSpacing: 1.2, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
        ← Back
      </Text>
    </Pressable>
  );
}

function NumField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <AdminInput
      theme={THEME}
      label={label}
      value={value}
      onChangeText={(v) => onChange(v.replace(/[^0-9]/g, ""))}
      keyboardType="numeric"
      hint={hint}
    />
  );
}
